import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import React, { useMemo } from 'react';
import { useChatHistory } from '../hooks/useChatHistory';
import { useStreamingConnection } from '../hooks/useStreamingConnection';
require('@chatscope/chat-ui-kit-styles/dist/default/styles.min.css');
require('./styles/style-overrides.css');

import {
  Avatar,
  ChatContainer,
  MainContainer,
  Message,
  MessageInput,
  MessageList,
  type MessageInputProps,
} from '@chatscope/chat-ui-kit-react';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import {
  PostDetails_ActivePost_Fragment,
  PostDetailsFragment,
  PostOrder,
  useCreatePostReactionMutation,
  useCreatePrivatePostMutation,
} from '../__generated__/api.graphql';
import { CLINICIANS_AGENT_USERNAME } from '../constants';
import AgentIcon from '../images/ai-bot.svg';

import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import {
  ArrowDown2,
  MessageCircle,
  ThumbsDown,
  ThumbsUp,
} from '@lifeomic/chromicons';
import EmptyPageState from '@lifeomic/phc-web-toolkit/src/components/EmptyPageState';
import {
  useActiveAccountId,
  useActiveProjectCohortId,
  useActiveProjectId,
  useFeatures,
  useLocalStorageState,
  useMixpanel,
  useNotify,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import { apiHooks } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import clsx from 'clsx';
import { defineMessages, useIntl } from 'react-intl';
import { v4 } from 'uuid';
import { text } from '../text';
import { ChatMessage } from './ChatMessage';
import { Citation } from './Citation';
import {
  ChatAttachmentMessage,
  ChatAttachmentNode,
} from './ChatAttachmentMessage';
import {
  AttachmentViewerContext,
  AttachmentViewerModal,
} from '@lifeomic/phc-web-toolkit/src/components/Messaging/AttachmentViewer';
import { ModelSelect } from '@lifeomic/phc-web-toolkit/src/components/AITemplates/GenerationMenu/ModelSelect';

const fhirService = new FhirService('Person');

const createEmptyAttachmentsV2 = () => ({
  __typename: 'AttachmentConnection' as const,
  pageInfo: {
    __typename: 'PageInfo' as const,
    endCursor: null,
    hasNextPage: false,
  },
  edges: [],
});

const messages = defineMessages({
  deletedMessage: {
    id: 'agentChat.chat.deletedMessage',
    defaultMessage: '<DELETED>',
  },
  feedbackNegative: {
    id: 'agentChat.chat.feedbackNegative',
    defaultMessage: 'Needs Improvement',
  },
  feedbackPositive: {
    id: 'agentChat.chat.feedbackPositive',
    defaultMessage: 'Good Response',
  },
  leaveFeedbackTitle: {
    id: 'agentChat.chat.leaveFeedbackTitle',
    defaultMessage: 'Your feedback helps us improve our AI Chat experience',
  },
  placeholder: {
    id: 'agentChat.chat.placeholder',
    defaultMessage: 'Ask a question…',
  },
  placeholderFeedback: {
    id: 'agentChat.chat.placeholderFeedback',
    defaultMessage: 'Let us know how we can improve…',
  },
  scrollToBottom: {
    id: 'agentChat.chat.scrollToBottom',
    defaultMessage: 'Latest Messages',
  },
  citationTitle: {
    id: 'agentChat.chat.citationTitle',
    defaultMessage: 'Citation Details',
  },
  errorLoadingMore: {
    id: 'agentChat.chat.errorLoadingMore',
    defaultMessage:
      'Error loading more messages. If this continues, please notify support.',
  },
  modelLabel: {
    id: 'agentChat.chat.modelLabel',
    defaultMessage: 'Model (optional)',
  },
  modelDefault: {
    id: 'agentChat.chat.modelDefault',
    defaultMessage: 'Default',
  },
});

const getPaddingTop = (
  theme: Theme,
  loadingMore: boolean,
  scrollToBottomVisible: boolean,
) => {
  if (loadingMore && scrollToBottomVisible) return theme.spacing(7.5);
  if (loadingMore) return theme.spacing(2.5);
  if (scrollToBottomVisible) return theme.spacing(5);
  return 0;
};

const useStyles = makeStyles((theme: Theme) => ({
  mainContainer: ({
    loadingMore,
    scrollToBottomVisible,
  }: {
    loadingMore: boolean;
    scrollToBottomVisible: boolean;
  }) => ({
    paddingTop: getPaddingTop(theme, loadingMore, scrollToBottomVisible),
  }),
  chatRoot: ({
    loadingMore,
    scrollToBottomVisible,
  }: {
    loadingMore: boolean;
    scrollToBottomVisible: boolean;
  }) => ({
    overflow: 'hidden',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    height:
      loadingMore || scrollToBottomVisible
        ? `calc(100% - ${getPaddingTop(
            theme,
            loadingMore,
            scrollToBottomVisible,
          )}px)`
        : '100%',
  }),
  startingContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    justifyItems: 'space-between',
    padding: '1rem',
  },
  typing: {
    backgroundColor: 'transparent',
  },
  startingMessage: {
    width: '100%',
  },
  fullWidth: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullHeight: {
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  feedbackTextArea: {
    width: '100%',
    padding: theme.spacing(1),
    borderColor: theme.palette.divider,
  },
  scrollToBottomButton: {
    border: `solid ${theme.pxToRem(1)} ${theme.palette.primary.main}`,
    borderRadius: theme.spacing(2),
    boxShadow: theme.boxShadows.table,
    paddingLeft: theme.spacing(2),
    '&:hover, &:focus': {
      opacity: 1,
    },
  },
  scrollToBottomContainer: {
    zIndex: 2,
    position: 'absolute',
    top: theme.spacing(-6), // negative to hide it
    transition: 'top 0.3s ease',
  },
  scrollToBottomContainerVisible: {
    top: theme.spacing(2),
  },
  attachmentViewerImage: {
    maxWidth: '100%',
    maxHeight: '100vh',
    objectFit: 'contain',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  modelSelectContainer: {
    padding: theme.spacing(1, 2, 2),
    width: '100%',
    display: 'flex',
  },
  chatColumn: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minWidth: 0,
  },
  chatContainer: {
    flex: 1,
    minHeight: 0,
  },
}));

export const Chat = ({
  patient,
  isMemberChatHistory,
  agentUsername = CLINICIANS_AGENT_USERNAME,
  userIdsOverride,
}: {
  patient?: fhir.Patient;
  isMemberChatHistory?: boolean;
  agentUsername?: string;
  userIdsOverride?: string[];
}) => {
  const { formatMessage } = useIntl();
  const mixpanel = useMixpanel();
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [componentLoaded, setComponentLoaded] = React.useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = React.useState(false);
  const [comment, setComment] = React.useState('');
  const [currentReference, setCurrentReference] = React.useState<string>('');
  const [hoveredPostId, setHoveredPostId] = React.useState<string | null>(null);
  const [selectedModelId, setSelectedModelId] = useLocalStorageState<
    string | undefined
  >('qa-agent-selected-model-id', undefined);
  const modelsQuery = apiHooks.useAPIQuery('GET /v1/agents-v2/models', {}) as {
    data?: {
      models: { model_id: string; model_label: string }[];
    };
  };
  const getListRef = () => document.querySelector('[data-cs-message-list]');
  const heightRef = React.useRef<number | null>(null);
  const accountId = useActiveAccountId();
  const projectId = useActiveProjectId();
  const cohortId = useActiveProjectCohortId();
  const attachmentViewer = React.useContext(AttachmentViewerContext);
  const { activeAttachmentImage, setActiveAttachmentImage } =
    attachmentViewer ?? {};

  const hasFeature = useFeatures();
  const showModelSelect = hasFeature('fountainLifePowerUser');

  const [getUser, getUserState] = useService(fhirService.get);
  const [patientUsername, setPatientUsername] = React.useState('');
  const hasMadeFetch = !!getUserState.lastFetchAttempt;

  React.useEffect(() => {
    const fetchUser = async () => {
      const params = {
        identifier: 'http://lifeomic.com/fhir/username|',
        patient: patient?.id,
        _tag: `http://lifeomic.com/fhir/dataset|${projectId}`,
        cohort: cohortId,
      };

      return await getUser(accountId, params);
    };

    fetchUser().then(user => {
      setPatientUsername(
        user?.data?.entry[0]?.resource?.identifier?.[0]?.value || '',
      );
    });
  }, [getUser, patient?.id, accountId, hasMadeFetch, cohortId, projectId]);

  const {
    userId,
    loading,
    posts,
    endCursor,
    fetchMore,
    loadMore,
    startPolling,
    addEagerPost,
  } = useChatHistory({
    patientId: patient?.id,
    isMemberChatHistory,
    patientUsername,
    agentUsername,
    userIdsOverride,
  });

  const userIds = (
    userIdsOverride || [
      userId,
      agentUsername,
      ...(patient?.id ? [`scope?subject=${patient?.id}`] : []),
    ]
  ).filter(Boolean) as string[];

  const parentId = userIds.sort().map(encodeURIComponent).join('#');

  // TODO: subscribe to chunks and render them
  const { messages: streamingMessages } = useStreamingConnection(
    `conversation:${parentId}`,
  );

  // Set scroll position after loading more messages
  React.useEffect(() => {
    const listRef = getListRef();

    if (listRef && heightRef.current !== null && !loadingMore) {
      listRef.scrollTop = listRef.scrollHeight - heightRef.current;
      heightRef.current = 0;
    }
  }, [loadingMore]);

  const [notify] = useNotify();

  const onYReachStart = () => {
    if (loadingMore === true || (heightRef.current && heightRef?.current > 0)) {
      return;
    }
    setLoadingMore(true);

    // Remember message listRef scrollHeight
    const listRef = getListRef();
    heightRef.current = listRef ? listRef.scrollHeight : heightRef.current;

    setTimeout(async () => {
      try {
        await loadMore();
      } catch (error) {
        notify(formatMessage(messages.errorLoadingMore), 'error');
      } finally {
        setLoadingMore(false);
      }
    }, 1500);
  };

  const [isScrolledToBottom, setIsScrolledToBottom] = React.useState(true);

  const onChatScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const { offsetHeight, scrollTop, scrollHeight } = target;
    const scrollBottom = scrollHeight - scrollTop - offsetHeight;

    // < 50 because we don't show the scroll to bottom button if we're pretty close to the bottom
    setIsScrolledToBottom(scrollBottom < 50);
  };

  const scrollToBottom = () => {
    const listRef = getListRef();
    if (listRef) {
      listRef.scrollTop = listRef.scrollHeight;
    }
  };

  const classes = useStyles({
    loadingMore,
    scrollToBottomVisible: !isScrolledToBottom,
  });

  if (!isMemberChatHistory) {
    startPolling(3000);
  }
  const [createPrivatePostMutation] = useCreatePrivatePostMutation({});
  const [createPostReactionMutation] = useCreatePostReactionMutation({});
  const sendMessage: MessageInputProps['onSend'] = async (
    innerText: string,
  ) => {
    // uses innerText instead of innerHTML which contains tags.
    const message = innerText;
    // this will get overwritten by the real message when the server responds
    const eagerPost: PostDetailsFragment = {
      id: v4(),
      author: {
        __typename: 'User' as const,
        userId,
        profile: {
          displayName: 'You',
          __typename: 'UserProfile' as const,
        },
      },
      __typename: 'ActivePost' as const,
      createdAt: new Date().toISOString(),
      parentId: '',
      message,
      attachmentsV2: createEmptyAttachmentsV2(),
    };
    addEagerPost(eagerPost);

    const metadata = {
      cohort_id: cohortId,
      project_id: projectId,
      ...(selectedModelId ? { model_id: selectedModelId } : {}),
    };

    await createPrivatePostMutation({
      variables: {
        input: {
          userIds,
          post: {
            id: eagerPost.id,
            message,
            metadata: JSON.stringify(metadata),
          },
          createConversation: false,
        },
      },
    });
    mixpanel.track('zora_message_sent');

    if (!isMemberChatHistory) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore fetchMore has multiple signatures
      await fetchMore({
        variables: {
          userIds,
          filter: {
            order: PostOrder.NEWEST,
          },
        },
      });
    }
  };

  const sendFeedback = async (feedback: '👍' | '👎') => {
    if (!hoveredPostId) {
      return;
    }

    await createPostReactionMutation({
      variables: {
        input: {
          postId: hoveredPostId,
          type: feedback,
          comment: comment,
        },
      },
    });

    setHoveredPostId(null);
    setComment('');
    setShowFeedbackModal(false);
  };

  const postsToRender = useMemo(() => {
    const reversePosts = posts ? [...posts].reverse() : [];
    const finishedPosts = reversePosts.filter(
      post =>
        !streamingMessages.find(message => message.responseId === post.id),
    );
    const streamingPosts = streamingMessages.map(message => {
      const tempPost: PostDetails_ActivePost_Fragment = {
        id: message.responseId,
        message: message.payload,
        author: {
          userId: agentUsername,
          profile: { displayName: 'You', __typename: 'UserProfile' as const },
          __typename: 'User' as const,
        },
        createdAt: new Date(message.startedAt).toISOString(),
        parentId: '',
        __typename: 'ActivePost' as const,
        metadata: JSON.stringify(message.metadata),
        attachmentsV2: createEmptyAttachmentsV2(),
      };
      return tempPost;
    });

    return [...finishedPosts, ...streamingPosts];
  }, [posts, streamingMessages, agentUsername]);

  const rowsToRender = useMemo(
    () =>
      buildChatRowsForMessageList({
        posts: postsToRender,
      }),
    [postsToRender],
  );

  const currentUserIdForDirection = isMemberChatHistory
    ? patientUsername
    : userId;

  const lastPost = postsToRender[postsToRender.length - 1];
  const isWaitingOnResponse =
    lastPost &&
    (isLastPostUserMessage(lastPost, userId) || isEphemeralPost(lastPost));

  if (loading && streamingMessages.length === 0) {
    return (
      <div className={clsx(classes.fullWidth, classes.fullHeight)}>
        <DotLoader />
      </div>
    );
  }

  return (
    <div className={classes.chatRoot}>
      <Modal
        isOpen={showFeedbackModal && !isMemberChatHistory}
        onDismiss={() => setShowFeedbackModal(false)}
        title={formatMessage(messages.leaveFeedbackTitle)}
        size={1}
        fullWidth
        actions={
          <>
            <Button
              icon={ThumbsDown}
              variant="outlined"
              onClick={() => sendFeedback('👎')}
            >
              {formatMessage(messages.feedbackNegative)}
            </Button>
            <Button
              icon={ThumbsUp}
              variant="outlined"
              onClick={() => sendFeedback('👍')}
            >
              {formatMessage(messages.feedbackPositive)}
            </Button>
          </>
        }
      >
        <textarea
          placeholder={formatMessage(messages.placeholderFeedback)}
          className={classes.feedbackTextArea}
          onChange={content => setComment(content.target.value)}
        ></textarea>
      </Modal>

      <MainContainer className={classes.mainContainer}>
        <AttachmentViewerModal isOpen={!!activeAttachmentImage}>
          {!!activeAttachmentImage && (
            <img
              alt="Attachment"
              src={activeAttachmentImage}
              className={classes.attachmentViewerImage}
            />
          )}
        </AttachmentViewerModal>
        <div
          className={clsx(
            classes.fullWidth,
            classes.scrollToBottomContainer,
            !isScrolledToBottom && classes.scrollToBottomContainerVisible,
          )}
        >
          <Button
            color="inverse"
            trailingIcon={ArrowDown2}
            onClick={scrollToBottom}
            className={classes.scrollToBottomButton}
          >
            {formatMessage(messages.scrollToBottom)}
          </Button>
        </div>

        <ChatContainer className={classes.chatContainer}>
          <MessageList
            onScroll={onChatScroll}
            typingIndicator={null} // we render our own
            loadingMore={false} // we render our own
            disableOnYReachWhenNoScroll={true}
            autoScrollToBottomOnMount={true}
            autoScrollToBottom={true}
            onYReachStart={() => {
              // prevents loading second page of messages when page loads
              if (!componentLoaded) {
                const listRef = getListRef();
                if (listRef) {
                  listRef.scrollTop = listRef.scrollHeight;
                }
                setComponentLoaded(true);
              }
              if (componentLoaded && endCursor) onYReachStart();
            }}
          >
            {loadingMore && (
              <div key={'load-more-indicator'} className={classes.fullWidth}>
                <DotLoader size={0} />
              </div>
            )}
            {(!postsToRender || postsToRender.length === 0) &&
              (isMemberChatHistory ? (
                <Box align="center" justify="center" fullHeight fullWidth>
                  <EmptyPageState
                    illustration={MessageCircle}
                    message={formatMessage(text.noMemberHistory)}
                  />
                </Box>
              ) : (
                <MessageList.Content>
                  <div className={classes.startingContent}>
                    <div>
                      <p>{formatMessage(text.helpContent1)}</p>
                      <p>{formatMessage(text.helpContent2)}</p>
                      <p>{formatMessage(text.helpContent3)}</p>
                      <p>{formatMessage(text.helpContent4)}</p>
                    </div>
                    <i className={classes.startingMessage}>
                      {formatMessage(text.startingMessage)}
                    </i>
                  </div>
                </MessageList.Content>
              ))}

            {rowsToRender.map(row => {
              if (row.kind === 'post') {
                return (
                  <ChatMessage
                    onMouseOver={() => setHoveredPostId(row.post.id)}
                    key={row.key}
                    post={row.post}
                    nextPost={row.nextPost}
                    onClickFeedback={() => setShowFeedbackModal(true)}
                    onClickCitation={setCurrentReference}
                    isHovered={hoveredPostId === row.post.id}
                    currentUserId={currentUserIdForDirection}
                  />
                );
              }

              return patient ? (
                <ChatAttachmentMessage
                  key={row.key}
                  post={row.post}
                  attachment={row.attachment}
                  patient={patient}
                  currentUserId={currentUserIdForDirection}
                  onOpenImage={url => {
                    if (setActiveAttachmentImage) {
                      setActiveAttachmentImage(url);
                      return;
                    }

                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                  onOpenPdf={url =>
                    window.open(url, '_blank', 'noopener,noreferrer')
                  }
                />
              ) : null;
            })}
            {isWaitingOnResponse && (
              <Message
                key={'typing-indicator'}
                model={{
                  message: '',
                  direction: 'incoming',
                  position: 'single',
                }}
              >
                <Avatar name={''} src={AgentIcon} />
                <Message.CustomContent>
                  <DotLoader size={0} />
                </Message.CustomContent>
              </Message>
            )}
          </MessageList>

          {!isMemberChatHistory && (
            <MessageInput
              attachButton={false}
              placeholder={formatMessage(messages.placeholder)}
              onSend={sendMessage}
              sendButton={false}
              plaintextOnly
            />
          )}
        </ChatContainer>

        {patient && (
          <Citation
            patient={patient}
            reference={currentReference}
            onCitationProcessed={() => setCurrentReference('')}
          />
        )}
      </MainContainer>
      {!isMemberChatHistory && showModelSelect && (
        <Box direction="column" className={classes.modelSelectContainer}>
          <ModelSelect
            modelId={selectedModelId}
            setModelId={setSelectedModelId}
            models={modelsQuery.data?.models ?? []}
            hideLabel={true}
          />
        </Box>
      )}
    </div>
  );
};

type ChatRow =
  | {
      kind: 'post';
      key: string;
      post: PostDetailsFragment;
      nextPost?: PostDetailsFragment;
    }
  | {
      kind: 'attachment';
      key: string;
      post: PostDetails_ActivePost_Fragment;
      attachment: ChatAttachmentNode;
    };

const buildChatRowsForMessageList = ({
  posts,
}: {
  posts: PostDetailsFragment[];
}): ChatRow[] => {
  return posts.flatMap((post, index) => {
    const nextPost = posts[index + 1];

    const postRow: ChatRow = {
      kind: 'post',
      key: post.id,
      post,
      nextPost,
    };

    if (post.__typename !== 'ActivePost') {
      return [postRow];
    }

    const attachmentRows: ChatRow[] = post.attachmentsV2.edges.map(edge => ({
      kind: 'attachment',
      key: `${post.id}#attachment#${edge.node.id}`,
      post,
      attachment: edge.node,
    }));

    // Attachments first, then caption/message.
    return [...attachmentRows, postRow];
  });
};

function isEphemeralPost(post: PostDetailsFragment): boolean {
  return (
    post.__typename === 'ActivePost' &&
    JSON.parse(post.metadata || '{}').type === 'ephemeral'
  );
}

function isLastPostUserMessage(
  post: PostDetailsFragment,
  userId: string,
): boolean {
  return post.__typename === 'ActivePost' && post.author?.userId === userId;
}
